import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Loader2, LayoutGrid, FolderPlus, Trash2, Wand2, ShieldAlert, Skull, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { SkeletonRows } from "@/components/common/Skeleton";
import { TID } from "@/lib/testIds";

/**
 * Fiori Catalog-based Role Engineering.
 * Lets an architect engineer a PFCG-style role from Fiori Business
 * Catalogs / Groups (the real SAP Fiori launchpad assignment unit) instead
 * of a raw T-code list — every resolved app's T-codes still go through the
 * exact same Secure-by-Design SoD + critical-access pipeline as any other
 * role (`POST /api/roles/generate-from-catalogs`).
 */
export function FioriCatalogRolesPanel({ onGenerated }) {
    const [catalogs, setCatalogs] = useState([]);
    const [loadingCatalogs, setLoadingCatalogs] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedCatalogs, setSelectedCatalogs] = useState(new Set());

    const [groups, setGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState(new Set());
    const [groupName, setGroupName] = useState("");
    const [creatingGroup, setCreatingGroup] = useState(false);

    const [roleName, setRoleName] = useState("");
    const [roleDesc, setRoleDesc] = useState("");
    const [generating, setGenerating] = useState(false);
    const [result, setResult] = useState(null);

    const loadCatalogs = useCallback(async (q) => {
        setLoadingCatalogs(true);
        try {
            const { data } = await api.get("/fiori-catalogs", { params: q ? { search: q } : {} });
            setCatalogs(data);
        } catch {
            setCatalogs([]);
        } finally {
            setLoadingCatalogs(false);
        }
    }, []);
    const loadGroups = useCallback(async () => {
        try {
            const { data } = await api.get("/fiori-groups");
            setGroups(data);
        } catch {
            setGroups([]);
        }
    }, []);
    useEffect(() => { loadCatalogs(""); loadGroups(); }, [loadCatalogs, loadGroups]);
    useEffect(() => {
        const t = setTimeout(() => loadCatalogs(search), 350);
        return () => clearTimeout(t);
    }, [search, loadCatalogs]);

    const toggleCatalog = (id) => {
        setSelectedCatalogs((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleGroup = (id) => {
        setSelectedGroups((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const createGroup = async () => {
        if (!groupName.trim()) { toast.error("Name the Fiori Group first"); return; }
        if (selectedCatalogs.size === 0) { toast.error("Select at least one catalog for this group"); return; }
        setCreatingGroup(true);
        try {
            await api.post("/fiori-groups", {
                name: groupName.trim(),
                catalog_ids: Array.from(selectedCatalogs),
            });
            toast.success(`Fiori Group "${groupName.trim()}" saved with ${selectedCatalogs.size} catalog(s)`);
            setGroupName("");
            setSelectedCatalogs(new Set());
            await loadGroups();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save Fiori Group");
        } finally {
            setCreatingGroup(false);
        }
    };

    const deleteGroup = async (id) => {
        try {
            await api.delete(`/fiori-groups/${id}`);
            setSelectedGroups((prev) => { const n = new Set(prev); n.delete(id); return n; });
            await loadGroups();
        } catch {
            toast.error("Failed to delete Fiori Group");
        }
    };

    const totalSelectedApps = useMemo(() => {
        const fromCatalogs = catalogs.filter((c) => selectedCatalogs.has(c.catalog_id)).reduce((s, c) => s + c.app_count, 0);
        return fromCatalogs;
    }, [catalogs, selectedCatalogs]);

    const generateRole = async () => {
        if (!roleName.trim()) { toast.error("Name the role first"); return; }
        if (selectedCatalogs.size === 0 && selectedGroups.size === 0) {
            toast.error("Select at least one Fiori Catalog or Group");
            return;
        }
        setGenerating(true);
        setResult(null);
        try {
            const { data } = await api.post("/roles/generate-from-catalogs", {
                name: roleName.trim(),
                description: roleDesc.trim() || undefined,
                catalog_ids: Array.from(selectedCatalogs),
                group_ids: Array.from(selectedGroups),
            });
            setResult(data);
            toast.success(`Engineered "${data.name}" — ${data.sod_conflicts.length} SoD conflict(s), ${data.critical_access.length} critical hit(s)`);
            setRoleName("");
            setRoleDesc("");
            onGenerated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to engineer role from catalogs");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="space-y-4">
            <Panel
                eyebrow="Real SAP Fiori Launchpad Assignment Unit"
                title="Engineer a Role from Fiori Catalogs / Groups"
            >
                <p className="text-xs text-slate-500 font-mono mb-4">
                    Business Catalogs are the actual PFCG launchpad-assignment unit (technically tracked via
                    AGR_BUFFI in a live system). Select one or more catalogs — or a saved Group of catalogs —
                    and every resolved app&rsquo;s underlying T-codes will run through the exact same
                    Secure-by-Design SoD + critical-access pipeline as any T-code-based role.
                </p>

                <div className="grid lg:grid-cols-2 gap-4">
                    {/* Catalog picker */}
                    <div className="panel p-3.5 rounded-[4px]">
                        <div className="relative mb-2.5">
                            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                                data-testid={TID.fioriCatalogSearchInput}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search Business Catalogs…"
                                className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm pl-8 pr-3 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                            />
                        </div>
                        <div className="flex items-center justify-between text-[11px] font-mono text-slate-500 mb-1.5">
                            <span>{catalogs.length} catalogs</span>
                            <span className="text-cyan-400">{selectedCatalogs.size} selected · {totalSelectedApps} apps</span>
                        </div>
                        {loadingCatalogs ? (
                            <SkeletonRows rows={5} cols={2} />
                        ) : (
                            <div className="space-y-1 max-h-[360px] overflow-y-auto">
                                {catalogs.map((c) => (
                                    <label
                                        key={c.catalog_id}
                                        data-testid={TID.fioriCatalogRow(c.catalog_id)}
                                        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[4px] cursor-pointer transition-colors border ${
                                            selectedCatalogs.has(c.catalog_id)
                                                ? "bg-cyan-500/10 border-cyan-500/30"
                                                : "border-transparent hover:bg-white/[0.03]"
                                        }`}
                                    >
                                        <input
                                            data-testid={TID.fioriCatalogCheckbox(c.catalog_id)}
                                            type="checkbox"
                                            checked={selectedCatalogs.has(c.catalog_id)}
                                            onChange={() => toggleCatalog(c.catalog_id)}
                                            className="accent-cyan-500 shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs text-slate-200 truncate">{c.title}</div>
                                            <div className="text-[10px] font-mono text-slate-500">{c.module || "—"} · {c.app_count} apps · {c.tcode_count} t-codes</div>
                                        </div>
                                    </label>
                                ))}
                                {catalogs.length === 0 && (
                                    <p className="text-xs text-slate-500 font-mono text-center py-6">No Business Catalogs match this search.</p>
                                )}
                            </div>
                        )}
                        <div className="flex gap-2 mt-3">
                            <input
                                data-testid={TID.fioriGroupNameInput}
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                placeholder="Save selection as a Fiori Group…"
                                className="flex-1 bg-[var(--bg-base)] border border-white/10 rounded-sm px-2.5 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                            />
                            <button
                                data-testid={TID.fioriGroupCreateBtn}
                                onClick={createGroup}
                                disabled={creatingGroup}
                                className="btn-ghost inline-flex items-center gap-1.5 px-3 text-xs disabled:opacity-50"
                            >
                                {creatingGroup ? <Loader2 size={12} className="animate-spin" /> : <FolderPlus size={12} />}
                                Save Group
                            </button>
                        </div>
                    </div>

                    {/* Groups + generate */}
                    <div className="space-y-3">
                        <div className="panel p-3.5 rounded-[4px]">
                            <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Layers size={11} /> Saved Fiori Groups ({groups.length})</div>
                            {groups.length === 0 ? (
                                <p className="text-xs text-slate-500 font-mono py-2">No groups saved yet — bundle frequently-used catalogs above.</p>
                            ) : (
                                <div className="space-y-1 max-h-[130px] overflow-y-auto">
                                    {groups.map((g) => (
                                        <div
                                            key={g.id}
                                            data-testid={TID.fioriGroupRow(g.id)}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded-[4px] border ${selectedGroups.has(g.id) ? "bg-rose-500/10 border-rose-500/30" : "border-transparent hover:bg-white/[0.03]"}`}
                                        >
                                            <input
                                                data-testid={TID.fioriGroupCheckbox(g.id)}
                                                type="checkbox"
                                                checked={selectedGroups.has(g.id)}
                                                onChange={() => toggleGroup(g.id)}
                                                className="accent-rose-500 shrink-0"
                                            />
                                            <span className="text-xs text-slate-200 flex-1 truncate">{g.name}</span>
                                            <span className="text-[10px] font-mono text-slate-500 shrink-0">{g.catalog_ids.length} catalogs</span>
                                            <button data-testid={TID.fioriGroupDeleteBtn(g.id)} onClick={() => deleteGroup(g.id)} className="text-slate-500 hover:text-rose-400 shrink-0">
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="panel-elevated p-3.5 rounded-[4px]">
                            <div className="label-eyebrow mb-2">Engineer Role</div>
                            <input
                                data-testid={TID.catalogRoleNameInput}
                                value={roleName}
                                onChange={(e) => setRoleName(e.target.value)}
                                placeholder="Role name, e.g. Z_FIORI_FI_AP_CLERK"
                                className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm px-2.5 py-2 text-sm text-slate-200 focus:border-cyan-500/40 focus:outline-none mb-2"
                            />
                            <textarea
                                data-testid={TID.catalogRoleDescInput}
                                value={roleDesc}
                                onChange={(e) => setRoleDesc(e.target.value)}
                                placeholder="Optional description…"
                                rows={2}
                                className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm px-2.5 py-2 text-xs text-slate-300 focus:border-cyan-500/40 focus:outline-none resize-none mb-3"
                            />
                            <button
                                data-testid={TID.catalogRoleGenerateBtn}
                                onClick={generateRole}
                                disabled={generating}
                                className="btn-primary sheen w-full inline-flex items-center justify-center gap-2 py-2 text-xs disabled:opacity-50"
                            >
                                {generating ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                                Generate Role from Catalogs
                            </button>
                        </div>

                        {result && (
                            <div data-testid={TID.catalogRoleResultPanel} className="panel-elevated p-3.5 rounded-[4px] border border-cyan-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <LayoutGrid size={13} className="text-cyan-400" />
                                    <span className="text-sm text-white font-medium">{result.name}</span>
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-400 border border-white/10 ml-auto">
                                        {result.fiori_app_ids.length} apps · {result.tcodes.length} t-codes
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2 text-[11px] font-mono">
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-rose-500/10 text-rose-300 border border-rose-500/20">
                                        <ShieldAlert size={11} /> {result.sod_conflicts.length} SoD conflicts
                                    </span>
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/20">
                                        <Skull size={11} /> {result.critical_access.length} critical hits
                                    </span>
                                    <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 uppercase">{result.risk_level}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Panel>
        </div>
    );
}
